import {
  catchError,
  EMPTY,
  filter,
  from,
  map,
  mergeMap,
  of,
  take,
  tap,
} from 'rxjs';
import * as actions from '#/store/actions/actions.js';
import { setExtensionAllowed } from '#/store/slices/extensions/actions.js';
import scopeLogger from '#/lib/scope-logger.js';
import Kibana from '#/utils/trackKibana.js';

const console = scopeLogger('RMI:connectGateways');
const INTERNAL_ERRORS = ({ topic, payload }) =>
  topic === 'internal' && payload.type === 'error';
/**
 * @param {Object} dependencies
 * @param {HostGateway} dependencies.hostGateway
 * @param {MNSInterface} dependencies.mns
 * @param {import('redux').store} dependencies.store
 * @param {ConfigProvider} dependencies.configProvider
 */
export default function connectGateways({
  hostGateway,
  mns,
  store,
  configProvider,
  interactiveAPI,
}) {

  mns.message$.pipe(filter(INTERNAL_ERRORS)).subscribe((message) => {
    if (message.payload.code === 401) {
      hostGateway.requestNewToken();
    } else if (message.payload.code === 999) {
      // panic
      hostGateway.sendNotify.error({
        fatal: true,
        message: message.payload.message,
      });
    }
  });

  hostGateway.message$
    .pipe(filter(({ event_type }) => event_type === 'change_token'))
    .subscribe(({ new_token }) => {
      configProvider.patchConfig('token', new_token);
    });

  mns.onConnectionChange = (isConnected) => {
    store.dispatch(actions.mnsConnectionStateChange({ isConnected }));
  };

  {
    /*
    TODO: instead listening for `start` action integrate it
      to the action dispatcher, see handling cmd start below
    this block should:
    - listen for complete video config
    - subscribe/unsubscribe to the MNS topic for the vodeo's settings
    - pass received message to Kibana
     */
    let unsubscribe = () => {};
    let currentTopic;

    store.listener.startListening({
      actionCreator: actions.interactiveStart,
      effect(action) {
        const { subscribe, level = 4 } = action.payload.config?.log ?? {};

        Kibana.setLevel(Kibana.logLevels[level]);

        if (!subscribe) {
          return;
        }

        if (subscribe === currentTopic) {
          return;
        }

        unsubscribe();
        mns.subscribe(subscribe);
        unsubscribe = () => mns.unsubscribe(subscribe);
        currentTopic = subscribe;
      },
    });

    mns.message$
      .pipe(
        filter(({ topic }) => topic === currentTopic),
        tap(
          /**
           *
           * The only one message we expect here, atm, is the **LOG** settings update.
           *
           * @todo May be, it should be passed into config?
           *       It's also possible receiving some settings from the EDGE for extensions.
           *
           * @param {import('@megogo-interactive/paho-client').Message} message
           *
           * @example payload example: {
           *   "type": "video_settings",
           *   "cmd": "update",
           *   "data": {
           *    "level": 4
           *    }
           *   }
           */
          (message) => {
          const { cmd, type, data } = message.payload;
          if (cmd ==='update' && type === 'video_settings') {
            Kibana.setLevel(Kibana.logLevels[data.level]);
          }
        }),
      )
      .subscribe();
  }

  {
    /*
TODO: Disable that action when all `time` related changes migrated to components
  e.g. emoji.displayQueue, extending every other actions with current time, etc.
  Also: replace store listener updateTimer action
  Also: move 01-app middleware logic into timeService
*/
    hostGateway.position$
      .pipe(map(actions.updateTimer))
      .subscribe(store.dispatch);

    const mapCmdToAction = new Map([
      ['stop', actions.interactiveStop],
      ['pause', actions.interactivePause],
      ['resume', actions.interactiveResume],
      ['zone-resize', actions.interactiveResize],
      ['settings_changed', setExtensionAllowed],
    ]);

    hostGateway.message$.pipe(
/*
      tap((mess) => {
        console.log(`Handle income message "${mess.cmd}"`, mess.payload);
      }),
*/
      mergeMap((message) => {
        switch (true) {
          case mapCmdToAction.has(message.cmd): {
            const action = mapCmdToAction.get(message.cmd)(message.payload);
            return of(store.dispatch(action));
          }
          case message.cmd === 'start': {
            console.warn('TODO: add abort controller to cancel outdated requests');
            const mergeWithMessage = (config) => ({
              ...message.payload,
              config,
            });

            return from(interactiveAPI.getConfig(message.payload)).pipe(
              catchError((error) => {
                console.error('Error fetching config', error);
                return EMPTY;
              }),
              tap(() => {
                console.warn('TODO: reimplement subscribe for log level for Kibana');
              }),
              map(mergeWithMessage),
              map(actions.interactiveStart),
              map(store.dispatch),
            );
          }
          default:
            // console.log('No action for message');
            return EMPTY;
        }
      }),
    ).subscribe({
/*
      next(action) {
        console.debug('action dispatched', action);
      },
*/
      error(err) {
        console.error('Error in message handling', err);
      },
/*
      done() {
        console.info('Completed, but should not');
      }
*/
    });
  }

  configProvider.config.pipe(
    take(1),
    tap((config) => {
      store.dispatch(actions.interactiveCreate(config));
    })
  )
    .subscribe();

}
